import { useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, Divider, Tooltip, Typography, TextField } from '@mui/material'
import { Link } from 'react-router-dom'
import {
  PreviousPage,
  AccountInfo,
  AccountLinks,
  ClaimAccount,
  UnSdgs,
  MediaList,
  QuillHtmlParser,
  DescriptionPreview,
} from 'src/components'
import { ImageCarousel } from 'src/components/image-carousel'
import { IAccount, IImpact, IMedia, IProduct, IUnSdg } from 'src/types'
import { JOURNEY_PAGE_TEMPLATE_ID } from 'src/templates/constants'
import { FairtradeImpactBubbles } from 'src/templates/fairtrade/components/impact-bubbles'
import { useIsTablet } from 'src/hooks'
import ArrowIconLeft from 'src/assets/icons/arrow.icon-left'
import ArrowIcon from 'src/assets/icons/arrow.icon'
import { PageWrapper } from '../fairtrade.styled'
import { FairtradeFonts } from '../fairtrade-fonts'
import { FairtradeAccountCard } from './account.styled'
import { DetailsTitle } from 'src/components/account-details/account-details.styled'
import {
  TitleText,
  ProductsWrapper,
  ProductItem,
} from 'src/components/account-details/account-additional-styles.styled'
import { useStyles } from 'src/components/account-details/style'
import { FAIRTRADE_FONT_STACK, FAIRTRADE_SKY_BLUE } from '../constants'
import { DEFAULT_LANGUAGE, ROUTES } from 'src/constants'
import { AIGeneratedBanner } from 'src/components/ai-generated-banner'
import { isHtmlEmpty } from 'src/utils/is-html-empty.util'
import { ensureProductHomePath } from 'src/utils'
import { css } from '@emotion/css'

interface IProps {
  account: IAccount
  filteredAccountImpacts: IImpact[]
  unSdgs: IUnSdg[]
  allProducts: IProduct[]
}

export const FairtradeAccountTemplate = ({
  account,
  filteredAccountImpacts,
  unSdgs,
  allProducts,
}: IProps) => {
  const [t, i18n] = useTranslation()
  const location = useLocation()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const isTablet = useIsTablet()
  const classes = useStyles()
  const [searchTerm, setSearchTerm] = useState('')

  const fairtradeNavColor = account?.alternative_color || '#DBFF4C'
  const previousLabel = location.state?.previousLabel || ''
  const previousFrom = ensureProductHomePath(location.state?.from)

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
  }

  const filteredProducts = allProducts.filter(
    (product) =>
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product?.gtin || '').toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const hasMedia = Array.isArray(account.media) && account.media.length > 0
  const hasDescription = !isHtmlEmpty(account.description)
  const hasProducts = Array.isArray(allProducts) && allProducts.length > 0

  const accountMainMedia: IMedia[] = (account.media ?? []).filter((m) =>
    m?.type?.startsWith('image'),
  )
  const hasMainMediaCarousel = accountMainMedia.length > 0

  const showDetailsTitle = hasDescription || hasMedia
  const showDividerDescMedia = hasDescription && hasMedia

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft += -130
    }
  }

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft += 130
    }
  }

  return (
    <PageWrapper
      id="index__templates-fairtrade-account__pagewrapper__l90"
      sx={{ marginTop: '20px' }}
    >
      <FairtradeFonts />
      <Box
        id="index__templates-fairtrade-account__box__l92"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: '8px',
          position: 'relative',
        }}
      >
        {previousFrom ? (
          <Box
            id="index__templates-fairtrade-account__box__l102"
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              position: 'relative',
              zIndex: 1,

              px: {
                xs: '24px',
                md: '0px',
              },
            }}
          >
            <PreviousPage
              to={previousFrom}
              variant={JOURNEY_PAGE_TEMPLATE_ID.FAIRTRADE}
              label={previousLabel}
              backgroundColor={fairtradeNavColor}
              textColor="#111111"
            />
          </Box>
        ) : (
          <div
            id="index__templates-fairtrade-account__div__l125"
            style={{ visibility: 'hidden' }}
          />
        )}
        {account?.ai_generated && <ClaimAccount />}
      </Box>

      <Box
        id="index__templates-fairtrade-account__box__l130"
        sx={{
          px: {
            xs: '24px',
            md: '0px',
          },
          '& h2': {
            fontFamily: FAIRTRADE_FONT_STACK,
          },
        }}
      >
        <AccountInfo
          image={account.image_medium || account.image}
          country={account.country}
          region={account.region}
          accountName={account.account_holder}
          walletAddress={account.wallet_address}
          showWalletAddress={account.show_wallet_address}
          avatarSize={140}
        />
      </Box>

      {hasMainMediaCarousel && (
        <Box
          id="index__templates-fairtrade-account__main-media-carousel"
          sx={{
            mx: { xs: '6px', md: '0px' },
            mt: '20px',
            mb: '24px',
            borderRadius: '24px',
            overflow: 'hidden',
          }}
        >
          <ImageCarousel images={accountMainMedia} />
        </Box>
      )}

      <Box
        id="index__templates-fairtrade-account__box__l148"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: '28px',
          px: {
            xs: '24px',
            md: '0px',
          },
        }}
      >
        <AccountLinks
          socials={account.socials || {}}
          website={account.website}
          email={account.email}
        />
      </Box>

      {filteredAccountImpacts.length > 0 && (
        <>
          <Divider id="index__templates-fairtrade-account__divider__l169" sx={{ my: '20px' }} />
          <Box
            id="index__templates-fairtrade-account__box__l170"
            sx={{
              display: 'flex',
              gap: '25px',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Box
              id="index__templates-fairtrade-account__box__l178"
              onClick={scrollLeft}
              sx={{
                cursor: 'pointer',
                display: isTablet || filteredAccountImpacts.length < 4 ? 'none' : '',
              }}
            >
              <ArrowIconLeft />
            </Box>
            <FairtradeImpactBubbles
              items={filteredAccountImpacts as IImpact[]}
              ref={scrollContainerRef}
              withArrows={!(isTablet || filteredAccountImpacts.length < 4)}
              primaryColor={account?.primary_color}
              walletAddress={account?.wallet_address}
              showWalletAddress={account?.show_wallet_address}
              showBcInfo={account?.show_bc_info}
              accountImage={account?.image_small || account?.image}
              isImpactsPage={false}
              disabled={false}
              templateId={JOURNEY_PAGE_TEMPLATE_ID.FAIRTRADE}
            />

            <Box
              id="index__templates-fairtrade-account__box__l200"
              onClick={scrollRight}
              sx={{
                cursor: 'pointer',
                display: isTablet || filteredAccountImpacts.length < 4 ? 'none' : '',
              }}
            >
              <ArrowIcon />
            </Box>
          </Box>

          {account?.show_unsgd_account_page && unSdgs.length > 0 && (
            <>
              <Divider
                id="index__templates-fairtrade-account__divider__l213"
                sx={{ mt: '25px', mb: '20px' }}
              />

              <Box
                id="index__templates-fairtrade-account__box__l214"
                sx={{
                  // mb: '28px',
                  px: {
                    xs: '24px',
                    md: '0px',
                  },
                }}
              >
                <UnSdgs sdgs={unSdgs} />
              </Box>
            </>
          )}
          <Divider
            id="index__templates-fairtrade-account__divider__l245"
            sx={{ mt: '25px', mb: '28px' }}
          />
        </>
      )}

      <FairtradeAccountCard
        id="index__templates-fairtrade-account__fairtradeaccountcard__l231"
        sx={{
          mx: {
            xs: '6px',
            md: '0px',
          },
        }}
      >
        {showDetailsTitle && (
          <DetailsTitle
            id="index__templates-fairtrade-account__detailstitle__l250"
            variant="subtitle1"
            sx={{
              '&.MuiTypography-root': {
                fontFamily: FAIRTRADE_FONT_STACK,
              },
            }}
          >
            {t('about-us-label')}
          </DetailsTitle>
        )}

        {account.home_stats &&
        account.home_stats.trim() &&
        account.home_stats !== '<p><br></p>' ? (
          <Box
            id="index__templates-fairtrade-account__home-stats"
            sx={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              padding: { xs: '16px', md: '24px' },
              marginBottom: '20px',
              border: '1px solid rgba(23, 34, 49, 0.08)',
            }}
          >
            <QuillHtmlParser html={account.home_stats} fontSize="22px" />
          </Box>
        ) : null}

        {hasDescription && (
          <DescriptionPreview
            contentKey={`account-description`}
            toggleColor={FAIRTRADE_SKY_BLUE}
            collapsedHeight={320}
            sx={{ mb: '20px' }}
          >
            <QuillHtmlParser html={account.description} fontSize="20px" />
          </DescriptionPreview>
        )}

        {showDividerDescMedia && (
          <Divider
            id="index__templates-fairtrade-account__divider__l254"
            sx={{ borderColor: 'rgba(0,0,0,0.08);' }}
          />
        )}

        {hasMedia && <MediaList media={account.media} />}
      </FairtradeAccountCard>

      {hasProducts && (
        <Box
          id="index__templates-fairtrade-account__box__l254"
          sx={{
            px: {
              xs: '24px',
              md: '24px',
            },
          }}
        >
          <div id="index__templates-fairtrade-account__div__l269" className={TitleText}>
            {t('product-list-title')}
          </div>
          {allProducts.length > 1 && (
            <TextField
              id="index__templates-fairtrade-account__textfield__l271"
              label={t('account-products-search-placeholder')}
              variant="outlined"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{ marginBottom: '20px', width: '50%', minWidth: '200px' }}
            />
          )}
          <div id="index__templates-fairtrade-account__div__l279" className={ProductsWrapper}>
            {filteredProducts.map((el) => (
              <Box
                id="index__templates-fairtrade-account__box__l281"
                key={el.id}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  maxWidth: '120px',
                  alignItems: 'center',
                  gap: '4px',
                  position: 'relative',
                }}
              >
                {el?.ai_generated && <AIGeneratedBanner />}
                <Tooltip
                  id="index__templates-fairtrade-account__tooltip__l293"
                  key={el.id}
                  title={el.name}
                  PopperProps={{ className: classes.popper }}
                >
                  <Link
                    className={css(
                      ProductItem,
                      `background-image: url('${el.image_medium ?? el.image_small ?? el.image}');`,
                    )}
                    to={
                      i18n.language === DEFAULT_LANGUAGE
                        ? `/${el.simple_id}/${ROUTES.PRODUCTS_HOME}`
                        : `/${i18n.language}/${el.simple_id}/${ROUTES.PRODUCTS_HOME}`
                    }
                  />
                </Tooltip>
                <Typography
                  id="index__templates-fairtrade-account__typography__l306"
                  sx={{ color: '#000', textAlign: 'center', mt: '3px' }}
                  variant="body2"
                >
                  {el.name}
                </Typography>
                {el?.gtin && (
                  <Typography
                    id="index__templates-fairtrade-account__typography__l313"
                    sx={{ fontSize: '10px' }}
                    variant="body2"
                  >
                    {el.gtin}
                  </Typography>
                )}
              </Box>
            ))}
          </div>
        </Box>
      )}
    </PageWrapper>
  )
}
